// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import styles from '../Backstage.module.css'

interface ResolutionDropdownProps {
    value: string
    disabled?: boolean
    onChange: (value: string) => void
}

const OPTIONS: Array<{ value: string; label: string }> = [
    { value: '1K', label: '1K' },
    { value: '2K', label: '2K' },
    { value: '4K', label: '4K' },
]

export function ResolutionDropdown(props: ResolutionDropdownProps): ReactElement {
    const { value, disabled = false, onChange } = props
    const [open, setOpen] = useState(false)
    const [anchor, setAnchor] = useState<{ x: number; y: number; width: number } | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const menuRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!open) return
        const handlePointerDown = (e: PointerEvent) => {
            const target = e.target as Node | null
            if (menuRef.current && target && menuRef.current.contains(target)) return
            if (buttonRef.current && target && buttonRef.current.contains(target)) return
            setOpen(false)
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        window.addEventListener('pointerdown', handlePointerDown, { capture: true })
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('pointerdown', handlePointerDown, { capture: true } as any)
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [open])

    const openMenu = () => {
        if (disabled) return
        const rect = buttonRef.current?.getBoundingClientRect()
        if (!rect) return
        setAnchor({ x: rect.left, y: rect.top - 6, width: rect.width })
        setOpen(true)
    }

    const toggleMenu = () => {
        if (open) {
            setOpen(false)
        } else {
            openMenu()
        }
    }

    const selectedLabel = OPTIONS.find((opt) => opt.value === value)?.label ?? value

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={styles.aspectRatioTrigger}
                onClick={toggleMenu}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Resolution"
            >
                <span className={styles.aspectRatioTriggerLabel}>Res</span>
                <span className={styles.aspectRatioTriggerValue}>{selectedLabel}</span>
                <ChevronDown className={styles.aspectRatioTriggerIcon} aria-hidden="true" />
            </button>
            {open && anchor && (
                <div
                    ref={menuRef}
                    className={`${styles.dropdownMenu} ${styles.dropdownMenuUp}`}
                    style={{ left: anchor.x, top: anchor.y, minWidth: anchor.width }}
                    role="menu"
                >
                    {OPTIONS.map((opt) => {
                        const isSelected = opt.value === value
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                className={styles.dropdownItem}
                                data-selected={isSelected}
                                onClick={() => {
                                    onChange(opt.value)
                                    setOpen(false)
                                }}
                                role="menuitemradio"
                                aria-checked={isSelected}
                            >
                                <span className={styles.dropdownItemLabel}>{opt.label}</span>
                                {isSelected ? (
                                    <Check className={styles.dropdownItemCheck} aria-hidden="true" />
                                ) : null}
                            </button>
                        )
                    })}
                </div>
            )}
        </>
    )
}
